import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

export function useNavigationInterceptor(
  onIntercept: (nextUrl: string, proceed: () => void) => void,
  shouldIntercept: boolean = true,
) {
  const router = useRouter();
  const proceedRef = useRef<() => void>(() => {});
  const proceedCalledRef = useRef(false);
  const [interceptedUrl, setInterceptedUrl] = useState<string | null>(null);

  useEffect(() => {
    // As we can't conditionally call hooks, do this instead
    if (!shouldIntercept) return;

    const handleRouteChangeStart = (url: string) => {
      if (url === router.asPath || interceptedUrl || proceedCalledRef.current) {
        return;
      }

      setInterceptedUrl(url);

      proceedRef.current = () => {
        // We double enter into this code - so this
        // ref says that we've already called proceed
        // so when the `router.push` is called again
        // we bail out on the return
        proceedCalledRef.current = true;
        setInterceptedUrl(null);
        void router.push(url);
      };

      onIntercept(url, proceedRef.current);

      // This is the only way to cancel the navigation
      // by throwing an error
      router.events.emit('routeChangeError');
      throw 'Navigation cancelled for interception';
    };

    const handlePopState = ({ as }: { as: string }) => {
      if (interceptedUrl) return false;

      setInterceptedUrl(as);

      proceedRef.current = () => {
        // We double enter into this code - so this
        // ref says that we've already called proceed
        // so when the `router.push` is called again
        // we bail out on the return
        proceedCalledRef.current = true;
        setInterceptedUrl(null);
        // Replace so you don't add a new history entry
        void router.replace(as);
      };

      onIntercept(as, proceedRef.current);
      return false;
    };

    const delegatedClickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a') as HTMLAnchorElement | null;

      if (
        !link ||
        link.target === '_blank' ||
        link.hasAttribute('download') ||
        link.getAttribute('rel') === 'external'
      ) {
        return;
      }

      const href = link.href;

      event.preventDefault();
      event.stopPropagation();

      proceedRef.current = () => {
        window.location.href = href;
      };

      onIntercept(href, proceedRef.current);
    };

    document.body.addEventListener('click', delegatedClickHandler);

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.beforePopState(handlePopState);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.beforePopState(() => true);
      document.body.removeEventListener('click', delegatedClickHandler);
    };
  }, [router, interceptedUrl, onIntercept, shouldIntercept]);
}
